import { methodCreateExamByBook, methodRestartExamByBook } from "../../api/apibook/api";
import {
    methodCheckResult,
    methodCreateSimulate,
    methodFinishSection,
    methodOpenExam,
    methodUpdateQuestion,
} from "../../api/apiexam/api";
import { OpenReportByBook } from "../../api/Report";
import { Book } from "../../model/book/Book";
import { CreateExamOption, Exam, ExamKind, ExamStatus } from "../../model/exam/Exam";
import { QuestionAction } from "../../model/question/Question";
import { RateEventType } from "../../model/Rate";
import { BookReport } from "../../model/Report";
import { UserInput } from "../../model/template/UserInput";
import { sleep } from "../../tools/time";
import { actionSetBook, actionSetBookIDOrigin } from "../book/reducer";
import { AppThunk } from "../hooks";
import { selectQuestion, selectQuestionAction } from "../question/selector";
import { actionRecordEvent } from "../rate/action";
import { RootState } from "../ReduxStore";
import { DisplayMode, actionSetDisplayMode, actionSetReport } from "../root/reducer";
import { actionSetShowTranslate } from "../user/reducer";
import {
    ExamStage,
    _actionSetQuestionFlag,
    _actionSetSectionIndex,
    actionCleanExamRuntime,
    actionSetExam,
    actionSetExamShowResult,
    actionSetExamStage,
    actionSetFromURL,
    actionSetPageIndex,
} from "./reducer";
import { selectQuestionIndex, selectSection, selectSectionAction } from "./selector";

export async function prepareForOpenExam(e: Exam, dispatch: any, fromURL: string) {
    dispatch(actionSetShowTranslate(false));
    dispatch(actionCleanExamRuntime());
    dispatch(actionSetExam(e));
    dispatch(actionSetFromURL(fromURL || ""));
    if (e.cursor.section_index === 0 && e.cursor.question_index === 0) {
        dispatch(actionJumpSection(0));
    }
    if (e.cursor.question_index !== 0) {
        dispatch(actionSetExamStage(ExamStage.InExam));
    }
    if (e.template_id.length > 0) {
        dispatch(actionSetBookIDOrigin(e.template_id));
    }
    if (e.status === ExamStatus.Finished) {
        dispatch(actionSetExamShowResult(e.status === ExamStatus.Finished));
        dispatch(actionSetExamStage(ExamStage.Result));
    }
    await sleep(100);
    dispatch(actionSetDisplayMode(DisplayMode.ExamDispaly));
}

function enterExam(e: Exam, dispatch: any, navigate: any, fromURL: string) {
    prepareForOpenExam(e, dispatch, fromURL);
    navigate(`/exam/${e.id}`);
}

export const actionOpenExam = (examID: string, navigate: any, fromURL: string): AppThunk => {
    return async (dispatch: any, getState: () => RootState) => {
        methodOpenExam(dispatch, navigate, {
            exam_id: examID,
        }).then((rsp) => {
            if (!rsp) {
                return;
            }
            enterExam(rsp.exam, dispatch, navigate, fromURL);
        });
    };
};

export const actionCreateExamByBook = (
    id: string,
    navigate: any,
    fromURL: string,
    input?: UserInput
): AppThunk => {
    return async (dispatch: any, getState: () => RootState) => {
        const input2 = input || new UserInput();
        methodCreateExamByBook(dispatch, {
            temp_id: id,
            input: input2,
        }).then((rsp) => {
            dispatch(actionSetBook(rsp.book));
            enterExam(rsp.exam, dispatch, navigate, fromURL);
        });
    };
};

export const actionRestartExamByBook = (id: string, navigate: any, fromURL: string): AppThunk => {
    return async (dispatch: any, getState: () => RootState) => {
        methodRestartExamByBook(dispatch, {
            book_id: id,
        }).then((rsp) => {
            dispatch(actionSetBook(rsp.book));
            enterExam(rsp.exam, dispatch, navigate, fromURL);
        });
    };
};

export const actionOpenReportByBook = (book: Book, navigate: any): AppThunk => {
    return async (dispatch: any, getState: () => RootState) => {
        OpenReportByBook({
            templateID: book.id,
            afterSuccess: (report: BookReport) => {
                dispatch(actionSetBook(book));
                dispatch(actionSetReport(report));
                navigate(`/report/${book.id}`);
            },
            dispatch,
        });
    };
};

export const actionCreateSimulate = (
    option: CreateExamOption,
    navigate: any,
    fromURL: string
): AppThunk => {
    return async (dispatch: any, getState: () => RootState) => {
        methodCreateSimulate(dispatch, {
            option,
        }).then((rsp) => {
            enterExam(rsp.exam, dispatch, navigate, fromURL);
        });
    };
};

export const actionQuitExam = (navigate: any, fromURL: string): AppThunk => {
    return async (dispatch: any, getState: () => RootState) => {
        if (fromURL.length > 0 && !fromURL.startsWith("/exam")) {
            navigate(fromURL);
        } else {
            navigate("/");
        }
        await sleep(100); // 等待一下再清理runtime数据, 避免navigate还未生效时, ExamPage自动重新拉取exam数据
        dispatch(actionSetDisplayMode(DisplayMode.NoneDisplay));
        dispatch(actionCleanExamRuntime());
    };
};

export const finishExam = (exam: Exam, dispatch: any, navigate: any) => {
    methodCheckResult(dispatch, {
        exam_id: exam.id,
    }).then((rsp) => {
        if (exam.kind === ExamKind.Higher) {
            OpenReportByBook({
                templateID: exam.template_id,
                afterSuccess: (r: BookReport) => {
                    dispatch(actionSetReport(r));
                },
                dispatch,
            });
        }
        dispatch(actionJumpSection(0));
        dispatch(actionSetExam(rsp.exam));
        dispatch(actionSetExamStage(ExamStage.Result));
        dispatch(actionSetExamShowResult(true));
    });
    return;
};

export const actionNextSection = (navigate: any): AppThunk => {
    return async (dispatch: any, getState: () => RootState) => {
        const rootState = getState();
        const state = rootState.examRuntime;
        const sectionIndex = state.sectionIndex;
        const action = selectSectionAction(rootState);
        const section = state.exam.sections[sectionIndex];

        // frontend action
        const frontendAction = () => {
            // jump to review
            if (sectionIndex === state.exam.sections.length - 1) {
                return finishExam(state.exam, dispatch, navigate);
            }
            // next section
            dispatch(actionJumpSection(sectionIndex + 1));
            dispatch(actionSetExamStage(ExamStage.StartIntro));
        };

        // backend action: set section finish
        if (section.action.finished) {
            frontendAction();
        } else {
            let qas = new Map<number, QuestionAction>();
            for (let i = 0; i < section.questions.length; i++) {
                const q = section.questions[i];
                qas.set(q.index_key, q.action);
            }
            methodFinishSection(dispatch, {
                exam_id: state.exam.id,
                section_index: sectionIndex,
                section_action: { ...section.action, finished: true },
                question_actions: qas,
            }).then((rsp) => {
                dispatch(actionSetExam(rsp.exam));
                frontendAction();
            });
        }
    };
};

export const actionSetQuestionFlag = (f: boolean): AppThunk => {
    return async (dispatch: any, getState: () => RootState) => {
        const state = getState();
        const estate = state.examRuntime;
        const sectionAction = selectSectionAction(state);
        const q = selectQuestion(state);
        const action = selectQuestionAction(state);
        dispatch(_actionSetQuestionFlag(f));
        methodUpdateQuestion(dispatch, {
            exam_id: estate.exam.id,
            section_index: estate.sectionIndex,
            question_index: q.index_key,
            action: { ...action, flagged: f },
            section_action: sectionAction,
        });
    };
};

export const actionNextPage = (): AppThunk => {
    return async (dispatch: any, getState: () => RootState) => {
        const state = getState();
        const section = selectSection(state);
        const questionIndex = selectQuestionIndex(state);
        const maxQuestion = section.questions.length;
        dispatch(actionRecordEvent(RateEventType.Question, section.questions[questionIndex]));
        dispatch(actionSetShowTranslate(false));
        dispatch(actionSetExamShowResult(false));
        // 跳到下一个可访问的题
        for (let i = questionIndex + 1; i < maxQuestion; i++) {
            const q = section.questions[i];
            if (q.data.accessable && !q.skip) {
                dispatch(actionSetPageIndex(i + 1));
                return;
            }
        }
        // 最后一题时, 跳转结束页
        dispatch(actionSetExamStage(ExamStage.EndIntro));
    };
};

export const actionPrevPage = (): AppThunk => {
    return async (dispatch: any, getState: () => RootState) => {
        const state = getState();
        const section = selectSection(state);
        const questionIndex = selectQuestionIndex(state);
        dispatch(actionSetShowTranslate(false));
        dispatch(actionSetExamShowResult(false));
        // 跳到上一个可访问的题
        for (let i = questionIndex - 1; i >= 0; i--) {
            const q = section.questions[i];
            if (q.data.accessable && !q.skip) {
                dispatch(actionSetPageIndex(i + 1));
                return;
            }
        }
        // 第一页时, 无效果
    };
};

// 跳转到指定section, 并且设置pageIndex为第一个可访问题
export const actionJumpSection = (sectionIndex: number): AppThunk => {
    return async (dispatch: any, getState: () => RootState) => {
        dispatch(actionSetPageIndex(1));
        dispatch(_actionSetSectionIndex(sectionIndex));
        const state = getState();
        const section = state.examRuntime.exam.sections[sectionIndex];
        const maxQuestion = section.questions.length;
        // 跳到第一个可访问的题
        for (let i = 0; i < maxQuestion; i++) {
            const q = section.questions[i];
            if (q.data.accessable && !q.skip) {
                dispatch(actionSetPageIndex(i + 1));
                return;
            }
        }
    };
};
